import { Component, Input } from "@angular/core";
import { PortalService } from "../../services/portal-service.service";
import { Iso } from "src/app/models/downloads";
import { CristieUtils } from "../../core/utils.service";

@Component({
  standalone: false,
  selector: "app-installers",
  templateUrl: "./installers.component.html",
  styleUrls: ["./installers.component.scss"],
})
export class InstallersComponent {
  @Input() installers: any;
  @Input() product = "";
  criteria: string;
  selectedPlatform = "All";
  sortKey: "item" | "platform" | "version" | "size" | "date" = "date";
  sortDirection: "asc" | "desc" = "desc";
  constructor(private portalService: PortalService) {}

  async trackDownload(item: Iso) {
    const username = CristieUtils.decodeBase64(localStorage.getItem("email"));
    const itemName = item.url.substring(item.url.lastIndexOf("/") + 1);
    const extension = itemName.split(".").pop();
    const fileNameWithoutExtension = itemName.substring(0, itemName.lastIndexOf("."));
    const downloadName = item.latest ? `${fileNameWithoutExtension}-${item.version}.${extension}` : itemName;
    try {
      await this.portalService.TrackDownload(username, item.product, item.url);
      window.open(item.url, downloadName);
    } catch {
      return;
    }
  }

  async copyClipboard(text: string) {
    await navigator.clipboard.writeText(text);
  }

  sortBy(key: "item" | "platform" | "version" | "size" | "date"): void {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      return;
    }

    this.sortKey = key;
    this.sortDirection = key === "date" || key === "size" ? "desc" : "asc";
  }

  getSortIcon(key: "item" | "platform" | "version" | "size" | "date"): string {
    if (this.sortKey !== key) return "unfold_more";
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  get showPlatformColumn(): boolean {
    return this.product !== "VA";
  }

  get showPlatformFilter(): boolean {
    return this.showPlatformColumn && this.product !== "SYSBACK";
  }

  get platforms(): string[] {
    return this.product === "SYSBACK" ? ["AIX"] : ["All", "Windows", "Linux", "AIX", "Solaris"];
  }

  get displayedInstallers() {
    const filter = (this.criteria || "").trim().toLowerCase();
    const filtered = (this.installers || []).filter((item) => {
      if (this.showPlatformFilter && this.selectedPlatform !== "All" && item.platform !== this.selectedPlatform) return false;
      if (!filter) return true;

      const itemName = item.url.substring(item.url.lastIndexOf("/") + 1).toLowerCase();
      const searchableValues = [itemName, item.platform, item.version, item.url];
      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(filter),
      );
    });

    return [...filtered].sort((a, b) => this.compareItems(a, b));
  }

  private compareItems(a: any, b: any): number {
    const direction = this.sortDirection === "asc" ? 1 : -1;
    switch (this.sortKey) {
      case "item":
        return (
          direction *
          this.getItemName(a).localeCompare(this.getItemName(b), undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      case "platform":
        return (
          direction * String(a.platform || "").localeCompare(String(b.platform || ""), undefined, { numeric: true, sensitivity: "base" })
        );
      case "version":
        return (
          direction * String(a.version || "").localeCompare(String(b.version || ""), undefined, { numeric: true, sensitivity: "base" })
        );
      case "size":
        return direction * ((a.size || 0) - (b.size || 0));
      case "date":
      default:
        return direction * (new Date(a.lastModified || 0).getTime() - new Date(b.lastModified || 0).getTime());
    }
  }

  private getItemName(item: any): string {
    return item.url.substring(item.url.lastIndexOf("/") + 1);
  }
}
